import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Skeleton } from "@/components/skeleton";
import { RelatedTaskSnapshotCard } from "@/components/crm/related-task-snapshot-card";
import { EmptyState } from "@/components/ui/empty-state";
import { useDebounce } from "@/hooks/use-debounce";
import { useOrganizationMembers } from "@/hooks/use-organization-members";
import { formatCurrency, formatDate } from "@/lib/i18n";
import type { OrganizationMemberRecord } from "@/lib/organization-client";
import {
  buildCrmProfileSections as buildCrmCompanyProfileSections,
} from "@ease/crm/profile-sections";
import { useContactList } from "@/hooks/use-contacts";
import { useTaskList } from "@/hooks/use-tasks";
import { CrmProfileSectionsCard } from "@/components/crm/profile-sections-card";
import {
  createCompany,
  deleteCompany,
  linkCompanyContact,
  linkCompanyDeal,
  setCompanyParent,
  unlinkCompanyContact,
  unlinkCompanyDeal,
  updateCompany,
  useCompanyList,
  useCompanyProfile,
  type CompanyContactSummary,
  type CompanyDealSummary,
  type CompanyProfile,
  type CompanySummary,
  type CreateCompanyData,
  type UpdateCompanyData,
} from "@/hooks/use-companies";
import { useDealList } from "@/hooks/use-deals";
import { Building2, ExternalLink, Link2, Plus, Search, Users, BriefcaseBusiness, ArrowLeft, GitBranch, Landmark, Pencil, Trash2 } from "lucide-react";

const COMPANY_SIZE_OPTIONS = ["1-10", "11-50", "51-200", "201-500", "500+"] as const;
const OPEN_COMPANY_TASK_STATUSES = new Set(["pending", "in_progress"]);

function safeHostname(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

export default function CompaniesPage() {
  const { companyId } = useParams<{ companyId?: string }>();

  if (companyId) {
    return <CompanyDetailPage companyId={companyId} />;
  }

  return <CompanyListPage />;
}

function CompanyListPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const { data: organizationMembers } = useOrganizationMembers();
  const memberLabelById = useMemo(
    () => new Map((organizationMembers ?? []).map((member) => [member.id, formatOrganizationMemberLabel(member)])),
    [organizationMembers],
  );

  const { companies, total, nextCursor, isLoading, error, loadMore, refetch } = useCompanyList({
    search: debouncedSearch || undefined,
    limit: 25,
  });

  const handleCreate = async (data: CreateCompanyData) => {
    setIsCreating(true);
    setCreateError(null);
    try {
      await createCompany(data);
      setShowCreateModal(false);
      refetch();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create company");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-foreground)]">Companies</h1>
          {total > 0 && (
            <p className="text-sm text-[var(--color-muted-foreground)]">{total} companies</p>
          )}
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-sm font-medium text-[var(--color-primary-foreground)] hover:bg-[var(--color-primary-hover)] transition-colors"
        >
          <Plus className="h-4 w-4" /> Add Company
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {error}
          <button onClick={refetch} className="ml-2 underline">Retry</button>
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
        <input
          type="text"
          placeholder="Search companies..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] py-2 pl-9 pr-3 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
        />
      </div>

      {!isLoading && companies.length === 0 && !error ? (
        <EmptyState
          icon="companies"
          title="No companies yet"
          description="Add your first company to start tracking organizations and their contacts."
          actionLabel="Add company"
          onAction={() => setShowCreateModal(true)}
        />
      ) : (
        <>
          <CompanyTable
            companies={companies}
            isLoading={isLoading}
            memberLabelById={memberLabelById}
            onSelect={(id) => navigate(`/crm/companies/${id}`)}
          />
          {nextCursor && (
            <div className="flex justify-center py-4">
              <button
                onClick={loadMore}
                className="rounded-md border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)]"
              >
                Load more
              </button>
            </div>
          )}
        </>
      )}

      {showCreateModal && (
        <CreateCompanyModal
          onSubmit={handleCreate}
          onClose={() => setShowCreateModal(false)}
          isSubmitting={isCreating}
          error={createError}
        />
      )}
    </div>
  );
}

function CompanyDetailPage({ companyId }: { companyId: string }) {
  const navigate = useNavigate();
  const { profile, isLoading, error, refetch } = useCompanyProfile(companyId);
  const { deals: allDeals } = useDealList();
  const { contacts: allContacts } = useContactList({ limit: 200 });
  const {
    tasks: companyTasks,
    isLoading: isCompanyTasksLoading,
    error: companyTasksError,
  } = useTaskList({ companyId, limit: 10 });
  const {
    companies: parentCompanyCandidates,
    isLoading: areParentCompaniesLoading,
    error: parentCompaniesError,
  } = useCompanyList({ limit: 200 });
  const {
    data: organizationMembers,
    isLoading: areOrganizationMembersLoading,
    error: organizationMembersError,
  } = useOrganizationMembers();
  const [contactId, setContactId] = useState("");
  const [contactRole, setContactRole] = useState("");
  const [dealId, setDealId] = useState("");
  const [parentCompanyId, setParentCompanyId] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const availableDeals = useMemo(
    () => allDeals.filter((deal) => !deal.companyId || deal.companyId === companyId),
    [allDeals, companyId],
  );
  const availableContacts = useMemo(() => {
    const linked = new Set(profile?.contacts.map((contact) => contact.id) ?? []);
    return allContacts.filter((contact) => !linked.has(contact.id));
  }, [allContacts, profile]);
  const openCompanyTasks = useMemo(
    () => companyTasks.filter((task) => OPEN_COMPANY_TASK_STATUSES.has(task.status)),
    [companyTasks],
  );
  const visibleCompanyTasks = openCompanyTasks.slice(0, 4);
  const memberOptions = useMemo(() => organizationMembers ?? [], [organizationMembers]);
  const memberLabelById = useMemo(
    () => new Map(memberOptions.map((member) => [member.id, formatOrganizationMemberLabel(member)])),
    [memberOptions],
  );
  const parentCompanyOptions = useMemo(() => {
    const excludedCompanyIds = new Set([
      companyId,
      ...(profile?.hierarchy.children.map((child) => child.id) ?? []),
    ]);
    const optionsById = new Map<string, CompanySummary>();

    for (const company of parentCompanyCandidates) {
      if (!excludedCompanyIds.has(company.id)) {
        optionsById.set(company.id, company);
      }
    }

    const currentParent = profile?.hierarchy.parent;
    if (currentParent && !excludedCompanyIds.has(currentParent.id)) {
      optionsById.set(currentParent.id, currentParent);
    }

    return Array.from(optionsById.values());
  }, [companyId, parentCompanyCandidates, profile]);
  const parentCompanyIsKnown = parentCompanyId
    ? parentCompanyOptions.some((company) => company.id === parentCompanyId)
    : true;

  useEffect(() => {
    setParentCompanyId(profile?.hierarchy.parent?.id ?? "");
  }, [profile?.hierarchy.parent?.id]);

  const applyProfileAction = async (action: () => Promise<CompanyProfile>) => {
    setIsSaving(true);
    setActionError(null);
    try {
      await action();
      await refetch();
      setContactId("");
      setContactRole("");
      setDealId("");
      setParentCompanyId("");
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to update company");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async (data: UpdateCompanyData) => {
    setIsSaving(true);
    setActionError(null);
    try {
      await updateCompany(companyId, data);
      setShowEditModal(false);
      await refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to update company");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this company? Contacts and deals remain in CRM, but the company record will be archived.")) {
      return;
    }

    setIsSaving(true);
    setActionError(null);
    try {
      await deleteCompany(companyId);
      navigate("/crm/companies");
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to delete company");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-56 rounded-lg" />
        <Skeleton className="h-28 rounded-xl" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate("/crm/companies")}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to companies
        </button>
        <div className="rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {error ?? "Company not found"}
          <button onClick={refetch} className="ml-2 underline">Retry</button>
        </div>
      </div>
    );
  }

  const companyProfileSections = buildCrmCompanyProfileSections(profile.company.customFields, {
    sectionKeys: ["companyProfileSections", "crmProfileSections", "profileSections"],
  }).sections;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="space-y-2">
          <button
            onClick={() => navigate("/crm/companies")}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to companies
          </button>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">{profile.company.name}</h1>
            {profile.company.website ? (
              <a
                href={profile.company.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs text-primary hover:bg-accent"
              >
                {safeHostname(profile.company.website)}
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : null}
          </div>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Mounted company operating view with live contacts, linked deals, aggregate metrics, and hierarchy management.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowEditModal(true)}
            className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
          >
            <Pencil className="h-4 w-4" />
            Edit company
          </button>
          <button
            onClick={refetch}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Refresh company
          </button>
          <button
            onClick={handleDelete}
            disabled={isSaving}
            className="inline-flex items-center gap-2 rounded-md border border-destructive/30 px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 disabled:opacity-60"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      </div>

      {actionError && (
        <div className="rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {actionError}
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-4">
        <MetricCard icon={<Users className="h-4 w-4" />} label="Contacts" value={String(profile.metrics.contactCount)} />
        <MetricCard icon={<BriefcaseBusiness className="h-4 w-4" />} label="Open deals" value={String(profile.metrics.openDealCount)} />
        <MetricCard icon={<Landmark className="h-4 w-4" />} label="Pipeline value" value={formatCurrency(profile.metrics.totalDealValue * 100, "EUR", { maximumFractionDigits: 0 })} />
        <MetricCard icon={<GitBranch className="h-4 w-4" />} label="Hierarchy" value={profile.hierarchy.parent || profile.hierarchy.children.length > 0 ? "Linked" : "Standalone"} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.3fr)_minmax(320px,0.9fr)]">
        <div className="space-y-4">
          <section className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-foreground">Contacts</h2>
                <p className="text-sm text-muted-foreground">Link people to this company and mark the primary relationship.</p>
              </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_180px_auto]">
              <select
                value={contactId}
                onChange={(event) => setContactId(event.target.value)}
                className="rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">Select a contact</option>
                {availableContacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {[contact.firstName, contact.lastName].filter(Boolean).join(" ").trim() || contact.email || contact.id}
                  </option>
                ))}
              </select>
              <input
                value={contactRole}
                onChange={(event) => setContactRole(event.target.value)}
                placeholder="Role"
                className="rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
              <button
                onClick={() => applyProfileAction(() => linkCompanyContact(companyId, { personId: contactId, role: contactRole || undefined, isPrimary: profile.contacts.length === 0 }))}
                disabled={!contactId.trim() || isSaving}
                className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-60"
              >
                Link contact
              </button>
            </div>

            <div className="mt-4 space-y-3">
              {profile.contacts.length === 0 ? (
                <p className="text-sm text-muted-foreground">No contacts linked yet.</p>
              ) : (
                profile.contacts.map((contact) => (
                  <ContactRow
                    key={contact.id}
                    contact={contact}
                    onRemove={() => applyProfileAction(() => unlinkCompanyContact(companyId, contact.id))}
                  />
                ))
              )}
            </div>
          </section>

          <section className="rounded-2xl border border-border bg-card p-5">
            <div>
              <h2 className="text-base font-semibold text-foreground">Deals</h2>
              <p className="text-sm text-muted-foreground">Link open opportunities to the company and monitor aggregate value.</p>
            </div>

            <div className="mt-4 flex gap-3">
              <select
                value={dealId}
                onChange={(event) => setDealId(event.target.value)}
                className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">Select a deal</option>
                {availableDeals.map((deal) => (
                  <option key={deal.id} value={deal.id}>
                    {deal.title}
                  </option>
                ))}
              </select>
              <button
                onClick={() => applyProfileAction(() => linkCompanyDeal(companyId, dealId))}
                disabled={!dealId || isSaving}
                className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-60"
              >
                Link deal
              </button>
            </div>

            <div className="mt-4 space-y-3">
              {profile.deals.length === 0 ? (
                <p className="text-sm text-muted-foreground">No deals linked yet.</p>
              ) : (
                profile.deals.map((deal) => (
                  <DealRow
                    key={deal.id}
                    deal={deal}
                    memberLabelById={memberLabelById}
                    onRemove={() => applyProfileAction(() => unlinkCompanyDeal(companyId, deal.id))}
                  />
                ))
              )}
            </div>
          </section>
        </div>

        <div className="space-y-4">
          <section className="rounded-2xl border border-border bg-card p-5">
            <div>
              <h2 className="text-base font-semibold text-foreground">Hierarchy</h2>
              <p className="text-sm text-muted-foreground">Parent/child relationships are stored as company associations, not fake local state.</p>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
              <select
                value={parentCompanyId}
                onChange={(event) => setParentCompanyId(event.target.value)}
                disabled={areParentCompaniesLoading}
                aria-label="Select parent company"
                className="rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">
                  {areParentCompaniesLoading ? "Loading companies..." : "No parent company"}
                </option>
                {!parentCompanyIsKnown && (
                  <option value={parentCompanyId}>{parentCompanyId}</option>
                )}
                {parentCompanyOptions.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => applyProfileAction(() => setCompanyParent(companyId, parentCompanyId.trim() || null))}
                disabled={isSaving || areParentCompaniesLoading}
                className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-60"
              >
                Save parent
              </button>
            </div>
            {parentCompaniesError && (
              <p className="mt-2 text-xs text-[var(--color-destructive)]">
                Company selector is temporarily unavailable. Existing hierarchy remains visible.
              </p>
            )}

            <div className="mt-4 space-y-3">
              <div className="rounded-xl bg-muted/40 px-3 py-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Parent</p>
                {profile.hierarchy.parent ? (
                  <Link to={`/crm/companies/${profile.hierarchy.parent.id}`} className="mt-1 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline">
                    <Link2 className="h-3.5 w-3.5" />
                    {profile.hierarchy.parent.name}
                  </Link>
                ) : (
                  <p className="mt-1 text-sm text-muted-foreground">No parent company linked.</p>
                )}
              </div>
              <div className="rounded-xl bg-muted/40 px-3 py-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Children</p>
                {profile.hierarchy.children.length > 0 ? (
                  <div className="mt-2 space-y-2">
                    {profile.hierarchy.children.map((child) => (
                      <Link key={child.id} to={`/crm/companies/${child.id}`} className="block text-sm font-medium text-primary hover:underline">
                        {child.name}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="mt-1 text-sm text-muted-foreground">No child companies linked.</p>
                )}
              </div>
            </div>
          </section>

          <CompanyIntelligenceCard profile={profile} />

          <CrmProfileSectionsCard
            sections={companyProfileSections}
            title="Account profile"
            description="Configured account facts from tenant product, service, commerce, and support systems."
            className="rounded-2xl border-border bg-card p-5"
            titleClassName="text-base font-semibold normal-case tracking-normal text-foreground"
            descriptionClassName="text-muted-foreground"
            itemClassName="border-border bg-background"
            actionClassName="border-border text-foreground hover:bg-accent"
          />

          <RelatedTaskSnapshotCard
            title="Open Company Tasks"
            description="Current account follow-ups, SLA signals, evidence, and safe operator actions tied to this company."
            tasks={visibleCompanyTasks}
            openTaskCount={openCompanyTasks.length}
            isLoading={isCompanyTasksLoading}
            error={companyTasksError}
            viewAllHref={`/crm/tasks?companyId=${encodeURIComponent(companyId)}`}
            emptyMessage="No open tasks for this company."
            overflowMessage={(visibleCount, totalCount) => `Showing ${visibleCount} of ${totalCount} open tasks for this company.`}
            evidenceLabel="Company task reusable evidence"
          />

          <section className="rounded-2xl border border-border bg-card p-5">
            <h2 className="text-base font-semibold text-foreground">Company truth</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <MetaRow label="Industry" value={profile.company.industry ?? "Not set"} />
              <MetaRow label="Size" value={profile.company.size ?? "Not set"} />
              <MetaRow label="Owner" value={formatCompanyOwner(profile.company.ownerId, memberLabelById)} />
              <MetaRow label="Domain" value={profile.company.domain ?? "Not set"} />
              <MetaRow label="Website" value={profile.company.website ?? "Not set"} />
            </dl>
          </section>
        </div>
      </div>

      {showEditModal && (
        <EditCompanyModal
          company={profile.company}
          members={memberOptions}
          areMembersLoading={areOrganizationMembersLoading}
          membersError={organizationMembersError}
          onSubmit={handleUpdate}
          onClose={() => setShowEditModal(false)}
          isSubmitting={isSaving}
          error={actionError}
        />
      )}
    </div>
  );
}

function CompanyIntelligenceCard({ profile }: { profile: CompanyProfile }) {
  const primaryContact = getPrimaryCompanyContact(profile.contacts);
  const topContact = getTopCompanyContact(profile.contacts);
  const latestActivityContact = getLatestActivityCompanyContact(profile.contacts);
  const topDeal = getTopCompanyDeal(profile.deals);
  const nextCloseDeal = getNextCompanyDealClose(profile.deals);
  const averageLeadScore = getAverageCompanyLeadScore(profile.contacts);

  return (
    <section className="rounded-2xl border border-border bg-card p-5">
      <div>
        <h2 className="text-base font-semibold text-foreground">Account intelligence</h2>
        <p className="text-sm text-muted-foreground">
          Reusable contact, score, activity, and opportunity signals for this company.
        </p>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <CompanyInsightTile
          label="Primary contact"
          value={primaryContact ? formatCompanyContactName(primaryContact) : "Not set"}
          detail={primaryContact ? formatCompanyContactRole(primaryContact) : "Mark one linked contact as primary."}
        />
        <CompanyInsightTile
          label="Average lead score"
          value={averageLeadScore == null ? "No scores" : String(averageLeadScore)}
          detail={formatCompanyContactCount(profile.contacts.length)}
        />
      </div>

      <dl className="mt-4 space-y-3 text-sm">
        <CompanySignalRow
          label="Top contact score"
          value={topContact ? `${formatCompanyContactName(topContact)} · Score ${topContact.leadScore}` : "No contacts linked"}
          detail={topContact ? formatCompanyContactStatus(topContact) : "Link contacts to surface scoring evidence."}
        />
        <CompanySignalRow
          label="Last contact activity"
          value={latestActivityContact?.lastActivityAt ? formatDate(latestActivityContact.lastActivityAt) : "No activity yet"}
          detail={latestActivityContact ? formatCompanyContactName(latestActivityContact) : "Activity appears when linked contacts update."}
        />
        <CompanySignalRow
          label="Top opportunity"
          value={topDeal ? topDeal.title : "No linked deals"}
          detail={topDeal ? formatCompanyDealSignal(topDeal) : "Link deals to surface opportunity evidence."}
        />
        <CompanySignalRow
          label="Next close"
          value={nextCloseDeal?.expectedCloseDate ? formatDate(nextCloseDeal.expectedCloseDate) : "No close date"}
          detail={nextCloseDeal ? nextCloseDeal.title : "Expected close dates appear from linked deals."}
        />
      </dl>
    </section>
  );
}

function CompanyInsightTile({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div className="rounded-xl bg-muted/40 px-3 py-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm font-semibold text-foreground">{value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
    </div>
  );
}

function CompanySignalRow({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-t border-border pt-3 first:border-t-0 first:pt-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="max-w-[13rem] text-right">
        <p className="font-medium text-foreground">{value}</p>
        <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
      </dd>
    </div>
  );
}

function CompanyTable({
  companies,
  isLoading,
  memberLabelById,
  onSelect,
}: {
  companies: CompanySummary[];
  isLoading: boolean;
  memberLabelById: ReadonlyMap<string, string>;
  onSelect: (id: string) => void;
}) {
  if (isLoading && companies.length === 0) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-14 rounded-md" />
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border border-[var(--color-border)]">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface)]">
            <th className="px-4 py-3 text-left font-medium text-[var(--color-muted-foreground)]">Name</th>
            <th className="px-4 py-3 text-left font-medium text-[var(--color-muted-foreground)]">Industry</th>
            <th className="px-4 py-3 text-left font-medium text-[var(--color-muted-foreground)]">Size</th>
            <th className="px-4 py-3 text-left font-medium text-[var(--color-muted-foreground)]">Owner</th>
            <th className="px-4 py-3 text-left font-medium text-[var(--color-muted-foreground)]">Website</th>
            <th className="px-4 py-3 text-left font-medium text-[var(--color-muted-foreground)]">Created</th>
          </tr>
        </thead>
        <tbody>
          {companies.map((company) => (
            <tr
              key={company.id}
              onClick={() => onSelect(company.id)}
              className="stagger-item cursor-pointer border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-surface)] transition-colors"
            >
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 flex-shrink-0 text-[var(--color-muted-foreground)]" />
                  <span className="font-medium text-[var(--color-foreground)]">{company.name}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-[var(--color-muted-foreground)]">{company.industry ?? "-"}</td>
              <td className="px-4 py-3 text-[var(--color-muted-foreground)]">{company.size ?? "-"}</td>
              <td className="px-4 py-3 text-[var(--color-muted-foreground)]">
                <span className="inline-flex max-w-[12rem] items-center gap-1.5 truncate">
                  <Users className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{formatCompanyOwner(company.ownerId ?? null, memberLabelById)}</span>
                </span>
              </td>
              <td className="px-4 py-3">
                {company.website ? (
                  <a
                    href={company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
                  >
                    {safeHostname(company.website)}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="text-[var(--color-muted-foreground)]">-</span>
                )}
              </td>
              <td className="px-4 py-3 text-[var(--color-muted-foreground)]">
                {formatDate(company.createdAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContactRow({
  contact,
  onRemove,
}: {
  contact: CompanyContactSummary;
  onRemove: () => void;
}) {
  const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim() || contact.email || contact.id;
  const signals = getCompanyContactSignals(contact);
  return (
    <div className="stagger-item flex flex-col gap-3 rounded-xl border border-border bg-background/70 px-3 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 flex-1">
        <Link to={`/crm/contacts/${contact.id}`} className="text-sm font-medium text-primary hover:underline">
          {fullName}
        </Link>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {signals.map((signal) => (
            <span
              key={`${contact.id}:${signal}`}
              className="rounded-full border border-border bg-card px-2 py-0.5 text-xs text-muted-foreground"
            >
              {signal}
            </span>
          ))}
        </div>
      </div>
      <button onClick={onRemove} className="shrink-0 text-xs font-medium text-muted-foreground hover:text-foreground">
        Remove
      </button>
    </div>
  );
}

function DealRow({
  deal,
  memberLabelById,
  onRemove,
}: {
  deal: CompanyDealSummary;
  memberLabelById: ReadonlyMap<string, string>;
  onRemove: () => void;
}) {
  const signals = getCompanyDealSignals(deal, memberLabelById);
  return (
    <div className="stagger-item flex flex-col gap-3 rounded-xl border border-border bg-background/70 px-3 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 flex-1">
        <Link to={`/crm/deals/${deal.id}`} className="text-sm font-medium text-primary hover:underline">
          {deal.title}
        </Link>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {signals.map((signal) => (
            <span
              key={`${deal.id}:${signal}`}
              className="rounded-full border border-border bg-card px-2 py-0.5 text-xs text-muted-foreground"
            >
              {signal}
            </span>
          ))}
        </div>
      </div>
      <button onClick={onRemove} className="shrink-0 text-xs font-medium text-muted-foreground hover:text-foreground">
        Unlink
      </button>
    </div>
  );
}

function MetricCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        {icon}
        {label}
      </div>
      <p className="mt-3 text-2xl font-semibold text-foreground">{value}</p>
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium text-foreground">{value}</dd>
    </div>
  );
}

function formatOrganizationMemberLabel(member: { name: string | null; email: string }): string {
  return member.name?.trim() || member.email;
}

function formatCompanyOwner(ownerId: string | null, memberLabelById: ReadonlyMap<string, string>): string {
  if (!ownerId) return "Unassigned";
  return memberLabelById.get(ownerId) ?? ownerId;
}

function getCompanyContactSignals(contact: CompanyContactSummary): string[] {
  return [
    contact.role ?? "No role",
    contact.isPrimary ? "Primary" : null,
    `Lead score ${contact.leadScore}`,
    formatCompanyLabel(contact.engagementStatus),
    formatCompanyLabel(contact.lifecycleStage),
    contact.lastActivityAt ? `Last activity ${formatDate(contact.lastActivityAt)}` : null,
    contact.source ? `Source ${formatCompanyLabel(contact.source)}` : null,
  ].filter((signal): signal is string => Boolean(signal));
}

function getCompanyDealSignals(
  deal: CompanyDealSummary,
  memberLabelById: ReadonlyMap<string, string>,
): string[] {
  return [
    deal.value == null ? "No value" : formatCurrency(deal.value * 100, deal.currency || "EUR", { maximumFractionDigits: 0 }),
    formatCompanyLabel(deal.stageId),
    `Deal score ${deal.score}`,
    deal.expectedCloseDate ? `Expected close ${formatDate(deal.expectedCloseDate)}` : null,
    deal.ownerId ? `Owner ${formatCompanyOwner(deal.ownerId, memberLabelById)}` : null,
  ].filter((signal): signal is string => Boolean(signal));
}

function getPrimaryCompanyContact(contacts: CompanyContactSummary[]): CompanyContactSummary | null {
  return contacts.find((contact) => contact.isPrimary) ?? null;
}

function getTopCompanyContact(contacts: CompanyContactSummary[]): CompanyContactSummary | null {
  return [...contacts].sort((left, right) => right.leadScore - left.leadScore)[0] ?? null;
}

function getLatestActivityCompanyContact(contacts: CompanyContactSummary[]): CompanyContactSummary | null {
  return [...contacts]
    .filter((contact) => Boolean(contact.lastActivityAt))
    .sort((left, right) => Date.parse(right.lastActivityAt ?? "") - Date.parse(left.lastActivityAt ?? ""))[0] ?? null;
}

function getTopCompanyDeal(deals: CompanyDealSummary[]): CompanyDealSummary | null {
  return [...deals].sort((left, right) => {
    if (right.score !== left.score) return right.score - left.score;
    return (right.value ?? 0) - (left.value ?? 0);
  })[0] ?? null;
}

function getNextCompanyDealClose(deals: CompanyDealSummary[]): CompanyDealSummary | null {
  return [...deals]
    .filter((deal) => Boolean(deal.expectedCloseDate))
    .sort((left, right) => Date.parse(left.expectedCloseDate ?? "") - Date.parse(right.expectedCloseDate ?? ""))[0] ?? null;
}

function getAverageCompanyLeadScore(contacts: CompanyContactSummary[]): number | null {
  if (contacts.length === 0) return null;
  return Math.round(contacts.reduce((total, contact) => total + contact.leadScore, 0) / contacts.length);
}

function formatCompanyContactName(contact: CompanyContactSummary): string {
  return [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim() || contact.email || contact.id;
}

function formatCompanyContactRole(contact: CompanyContactSummary): string {
  return [contact.role, contact.isPrimary ? "Primary" : null].filter(Boolean).join(" · ") || "No role set";
}

function formatCompanyContactStatus(contact: CompanyContactSummary): string {
  return [
    formatCompanyLabel(contact.engagementStatus),
    formatCompanyLabel(contact.lifecycleStage),
    contact.role,
  ].filter(Boolean).join(" · ");
}

function formatCompanyContactCount(contactCount: number): string {
  if (contactCount === 0) return "No linked contacts";
  if (contactCount === 1) return "1 linked contact";
  return `${contactCount} linked contacts`;
}

function formatCompanyDealSignal(deal: CompanyDealSummary): string {
  const value = deal.value == null
    ? "No value"
    : formatCurrency(deal.value * 100, deal.currency || "EUR", { maximumFractionDigits: 0 });
  return `${value} · Score ${deal.score} · ${formatCompanyLabel(deal.stageId)}`;
}

function formatCompanyLabel(value: string | null): string {
  if (!value) return "";
  return value
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() + part.slice(1))
    .join(" ");
}

function EditCompanyModal({
  company,
  members,
  areMembersLoading,
  membersError,
  onSubmit,
  onClose,
  isSubmitting,
  error,
}: {
  company: CompanyProfile["company"];
  members: OrganizationMemberRecord[];
  areMembersLoading: boolean;
  membersError: string | null;
  onSubmit: (data: UpdateCompanyData) => void;
  onClose: () => void;
  isSubmitting: boolean;
  error: string | null;
}) {
  const [name, setName] = useState(company.name);
  const [domain, setDomain] = useState(company.domain ?? "");
  const [industry, setIndustry] = useState(company.industry ?? "");
  const [size, setSize] = useState(company.size ?? "");
  const [website, setWebsite] = useState(company.website ?? "");
  const [annualRevenue, setAnnualRevenue] = useState(
    company.annualRevenue == null ? "" : String(company.annualRevenue),
  );
  const [ownerId, setOwnerId] = useState(company.ownerId ?? "");
  const ownerIsKnown = ownerId ? members.some((member) => member.id === ownerId) : true;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: name.trim(),
      domain: domain.trim() || undefined,
      industry: industry.trim() || undefined,
      size: size || undefined,
      website: website.trim() || undefined,
      annualRevenue: annualRevenue ? Number(annualRevenue) : undefined,
      ownerId: ownerId || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-4 text-lg font-semibold text-[var(--color-foreground)]">Edit Company</h2>
        {error && (
          <div className="mb-4 rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-3 py-2 text-sm text-[var(--color-destructive)]">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-[var(--color-foreground)]">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm" autoFocus />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[var(--color-foreground)]">Domain</label>
            <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="acme.example" className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[var(--color-foreground)]">Industry</label>
            <input value={industry} onChange={(e) => setIndustry(e.target.value)} className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[var(--color-foreground)]">Size</label>
            <select value={size} onChange={(e) => setSize(e.target.value)} className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm">
              <option value="">Not set</option>
              {COMPANY_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[var(--color-foreground)]">Website</label>
            <input value={website} onChange={(e) => setWebsite(e.target.value)} className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[var(--color-foreground)]">Annual revenue</label>
            <input type="number" min="0" value={annualRevenue} onChange={(e) => setAnnualRevenue(e.target.value)} className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[var(--color-foreground)]">Owner</label>
            <select
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              disabled={areMembersLoading || Boolean(membersError)}
              aria-label="Assign company owner"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
            >
              <option value="">
                {areMembersLoading
                  ? "Loading owners..."
                  : membersError
                    ? "Owner list unavailable"
                    : "Unassigned"}
              </option>
              {!ownerIsKnown && (
                <option value={ownerId}>
                  {ownerId}
                </option>
              )}
              {members.map((member) => (
                <option key={member.id} value={member.id}>
                  {formatOrganizationMemberLabel(member)}
                </option>
              ))}
            </select>
            {membersError && (
              <p className="mt-1 text-xs text-[var(--color-destructive)]">
                Owner selection is temporarily unavailable. Existing owner assignment will be preserved.
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)]">Cancel</button>
            <button type="submit" disabled={!name.trim() || isSubmitting} className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] disabled:opacity-60">
              {isSubmitting ? "Saving..." : "Save company"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateCompanyModal({
  onSubmit,
  onClose,
  isSubmitting,
  error,
}: {
  onSubmit: (data: CreateCompanyData) => void;
  onClose: () => void;
  isSubmitting: boolean;
  error: string | null;
}) {
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [industry, setIndustry] = useState("");
  const [size, setSize] = useState("");
  const [website, setWebsite] = useState("");
  const [annualRevenue, setAnnualRevenue] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      domain: domain.trim() || undefined,
      industry: industry || undefined,
      size: size || undefined,
      website: website || undefined,
      annualRevenue: annualRevenue ? Number(annualRevenue) : undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-[var(--color-foreground)] mb-4">Add Company</h2>
        {error && (
          <div className="mb-4 rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-3 py-2 text-sm text-[var(--color-destructive)]">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-[var(--color-foreground)] mb-1">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm" autoFocus />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-foreground)] mb-1">Industry</label>
            <input value={industry} onChange={(e) => setIndustry(e.target.value)} className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-foreground)] mb-1">Domain</label>
            <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="acme.example" className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-foreground)] mb-1">Size</label>
            <select value={size} onChange={(e) => setSize(e.target.value)} className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm">
              <option value="">Not set</option>
              {COMPANY_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-foreground)] mb-1">Website</label>
            <input value={website} onChange={(e) => setWebsite(e.target.value)} className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-foreground)] mb-1">Annual revenue</label>
            <input type="number" min="0" value={annualRevenue} onChange={(e) => setAnnualRevenue(e.target.value)} className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)]">Cancel</button>
            <button type="submit" disabled={!name.trim() || isSubmitting} className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] disabled:opacity-60">
              {isSubmitting ? "Creating..." : "Create company"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
